import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormControlInput } from "../../../../components/organisms/Form/control";
import { ButtonT1 } from "../../../../components/atoms/Button/Button";
import { ManageGeneralFormProvider } from "../../../../components/organisms/Provider/ManageGeneralFormProvider";
import { FormCard, FormTitle, FormSubtitle, BackLink, ErrorMessage } from "../index.styles";
import { usePortalConfirmPasswordResetMutation } from "../../../../store/apiSlice/portalAuthApiSlice";

const ResetPassword = () => {
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { subdomain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [confirmPasswordReset, { isLoading }] = usePortalConfirmPasswordResetMutation();

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: async values => {
      setError("");
      try {
        await confirmPasswordReset({
          token,
          new_password: values.newPassword,
          new_password_confirm: values.confirmPassword,
        }).unwrap();
        setSubmitted(true);
      } catch (err) {
        setError("Failed to reset password. Please try again.");
      }
    },
  });

  if (!token) {
    return (
      <FormCard>
        <FormTitle>Invalid Reset Link</FormTitle>
        <FormSubtitle>This password reset link is invalid or has expired.</FormSubtitle>
        <BackLink onClick={() => navigate(`/portal/${subdomain}/auth/forgot_password`)}>
          Request a new link
        </BackLink>
      </FormCard>
    );
  }

  if (submitted) {
    return (
      <FormCard>
        <FormTitle>Password Reset Successful</FormTitle>
        <FormSubtitle>Your password has been successfully reset.</FormSubtitle>
        <BackLink onClick={() => navigate(`/portal/${subdomain}/auth/login`)}>
          Go to Sign in
        </BackLink>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <FormTitle>Reset Password</FormTitle>
      <FormSubtitle>Enter your new password below.</FormSubtitle>
      <ManageGeneralFormProvider formik={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-7">
            <FormControlInput
              name="newPassword"
              type="password"
              label="New Password"
              placeholder="New Password"
              variant="light"
              context="general"
              required
            />
          </div>
          <div className="mb-7">
            <FormControlInput
              name="confirmPassword"
              type="password"
              label="Confirm Password"
              placeholder="Confirm Password"
              variant="light"
              context="general"
              required
            />
          </div>

          <ButtonT1
            title={isLoading ? "Resetting..." : "Reset Password"}
            loading={isLoading}
            disabled={isLoading}
            type="submit"
            $variant="primary"
            $width="100%"
          />
        </form>
      </ManageGeneralFormProvider>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </FormCard>
  );
};

export default ResetPassword;
